import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { filterSubItemsByPermissions } from '@/utils/permissions';
import { getSidebarModulesForDynamicSidebar } from '@/config/sidebarConfig';
import api from '@/services/api';
import purchaseRequestService from '@/services/purchaseRequestService';
import toast from 'react-hot-toast';
import SidebarFlyout from './SidebarFlyout';
import { 
  ChevronRight,
  ChevronLeft
} from 'lucide-react';

// Helper function to scroll main content to top
const scrollMainToTop = (behavior = 'smooth') => {
  const mainElement = document.querySelector('main');
  if (mainElement) {
    mainElement.scrollTo({
      top: 0,
      left: 0,
      behavior: behavior
    });
  }
};

const DynamicSidebar = ({ user, isCollapsed, setIsCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { permissions, hasModule } = useAuth();
  const { socket, isConnected } = useWebSocket();
  const [userModules, setUserModules] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});
  const [badgeCounts, setBadgeCounts] = useState({});
  const [hoveredModule, setHoveredModule] = useState(null);
  const [hoveredModuleRect, setHoveredModuleRect] = useState(null);
  const closeTimeoutRef = useRef(null);
  const moduleRefs = useRef({});

  // Debug logging
  console.log('DynamicSidebar render:', { 
    user: !!user, 
    userModules: userModules.length, 
    isCollapsed,
    permissions: !!permissions 
  });

  // Get all available modules from centralized configuration
  const allModules = getSidebarModulesForDynamicSidebar();

  // Load user's assigned modules
  useEffect(() => {
    loadUserModules();
  }, [user, permissions]);

  const refreshSidebarCounts = useCallback(async () => {
    try {
      const counts = {};
      let procurementTotal = 0;

      const procurementModule = userModules.find((m) => m.id === 'purchase-requests');
      const allowedSubItems = procurementModule?.subItems?.map((s) => s.path) || [];

      if (allowedSubItems.includes('/purchase-requests/pending-approval')) {
        try {
          const resPending = await purchaseRequestService.getPendingApprovals();
          const pendingCount = resPending?.data?.data?.total || (resPending?.data?.data?.requests?.length ?? 0);
          counts['/purchase-requests/pending-approval'] = pendingCount;
          procurementTotal += pendingCount;
        } catch (e) {}
      }

      if (allowedSubItems.includes('/purchase-requests/procurement')) {
        try {
          const resProc = await purchaseRequestService.getPendingProcurement();
          const procCount = resProc?.data?.data?.total || (resProc?.data?.data?.requests?.length ?? 0);
          counts['/purchase-requests/procurement'] = procCount;
          procurementTotal += procCount;
        } catch (e) {}
      }

      if (allowedSubItems.includes('/purchase-requests/finance')) {
        try {
          const resFin = await purchaseRequestService.getPendingFinance();
          const resPay = await purchaseRequestService.getAllRequests({ status: 'payment_in_progress', current_stage: 'pay_vendor_stage', show_all: true });
          const finCount = resFin?.data?.data?.total || (resFin?.data?.data?.requests?.length ?? 0);
          const payCount = resPay?.data?.data?.total || (resPay?.data?.data?.requests?.length ?? 0);
          const paymentTotal = (finCount || 0) + (payCount || 0);
          counts['/purchase-requests/finance'] = paymentTotal;
          procurementTotal += paymentTotal;
        } catch (e) {}
      }

      counts['/purchase-requests'] = procurementTotal;

      try {
        const resGMD = await purchaseRequestService.getAllRequests({
          status: 'pending_md_approval',
          current_stage: 'md_approval_stage',
          show_all: true,
        });
        const gmdCount = resGMD?.data?.data?.total || (resGMD?.data?.data?.requests?.length ?? 0);
        counts['/gmd/dashboard'] = gmdCount;
      } catch (e) {}

      try {
        const resMessages = await api.get('/direct-messaging/unread-count');
        const count = resMessages?.data?.data?.count || 0;
        counts['/collaboration'] = count;
        counts['/collaboration/messages'] = count;
      } catch (e) {
        console.error('Failed to fetch unread messages count:', e);
      }

      setBadgeCounts(counts);
    } catch (err) {
      // silent fail
    }
  }, [userModules]);

  // Load counts for Purchase Requests sub-items
  useEffect(() => {
    const loadCounts = async () => {
      try {
        const counts = {};
        let procurementTotal = 0;
        
        // Get user's allowed procurement submodules
        const procurementModule = userModules.find(m => m.id === 'purchase-requests');
        const allowedSubItems = procurementModule?.subItems?.map(s => s.path) || [];
        
        // Only fetch counts for submodules the user has access to
        
        // Pending Approval count
        if (allowedSubItems.includes('/purchase-requests/pending-approval')) {
          try {
            const resPending = await purchaseRequestService.getPendingApprovals();
            const pendingCount = resPending?.data?.data?.total || (resPending?.data?.data?.requests?.length ?? 0);
            counts['/purchase-requests/pending-approval'] = pendingCount;
            procurementTotal += pendingCount;
          } catch (e) {}
        }
        
        // Procurement Approval count
        if (allowedSubItems.includes('/purchase-requests/procurement')) {
          try {
            const resProc = await purchaseRequestService.getPendingProcurement();
            const procCount = resProc?.data?.data?.total || (resProc?.data?.data?.requests?.length ?? 0);
            counts['/purchase-requests/procurement'] = procCount;
            procurementTotal += procCount;
          } catch (e) {}
        }
        
        // Payment Process count (sum of finance approval + payment in progress)
        if (allowedSubItems.includes('/purchase-requests/finance')) {
          try {
            const resFin = await purchaseRequestService.getPendingFinance();
            const resPay = await purchaseRequestService.getAllRequests({ status: 'payment_in_progress', current_stage: 'pay_vendor_stage', show_all: true });
            const finCount = resFin?.data?.data?.total || (resFin?.data?.data?.requests?.length ?? 0);
            const payCount = resPay?.data?.data?.total || (resPay?.data?.data?.requests?.length ?? 0);
            const paymentTotal = (finCount || 0) + (payCount || 0);
            counts['/purchase-requests/finance'] = paymentTotal;
            procurementTotal += paymentTotal;
          } catch (e) {}
        }
        
        // Set total for main Procurement module
        counts['/purchase-requests'] = procurementTotal;
        
        // GMD Approval count
        try {
          const resGMD = await purchaseRequestService.getAllRequests({ 
            status: 'pending_md_approval',
            current_stage: 'md_approval_stage',
            show_all: true
          });
          const gmdCount = resGMD?.data?.data?.total || (resGMD?.data?.data?.requests?.length ?? 0);
          counts['/gmd/dashboard'] = gmdCount;
        } catch (e) {}
        
        // Unread messages count for Collaboration
        try {
          const resMessages = await api.get('/direct-messaging/unread-count');
          const count = resMessages?.data?.data?.count || 0;
          counts['/collaboration'] = count;
          counts['/collaboration/messages'] = count;
        } catch (e) {
          console.error('Failed to fetch unread messages count:', e);
        }
        setBadgeCounts(counts);
      } catch (err) {
        // silent fail
      }
    };
    
    if (userModules.length > 0) {
      refreshSidebarCounts();
      const interval = setInterval(loadCounts, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [userModules]);

  // Listen for WebSocket events to update counts in real-time
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handlePurchaseRequestUpdate = (data) => {
      console.log('📢 Purchase request updated via WebSocket - refreshing counts');
      refreshSidebarCounts();
      
      // Show toast notification
      if (data?.message) {
        toast.success(data.message, {
          duration: 4000,
          icon: '🔔',
        });
      }
    };

    const handlePurchaseRequestCreated = (data) => {
      console.log('📢 New purchase request created');
      refreshSidebarCounts();
      
      toast.success(`New purchase request: ${data?.request_id || 'Created'}`, {
        duration: 4000,
        icon: '📝',
      });
    };

    const handlePurchaseRequestApproved = (data) => {
      console.log('📢 Purchase request approved');
      refreshSidebarCounts();
      
      toast.success(`Request ${data?.request_id || ''} approved at ${data?.stage || 'stage'}`, {
        duration: 4000,
        icon: '✅',
      });
    };

    // Handle direct message unread count updates in real-time
    const handleUnreadCountUpdate = (data) => {
      console.log('📬 Unread message count updated via WebSocket:', data);
      setBadgeCounts(prev => ({
        ...prev,
        '/collaboration': data.count || 0,
        '/collaboration/messages': data.count || 0
      }));
    };

    socket.on('purchase_request_updated', handlePurchaseRequestUpdate);
    socket.on('purchase_request_created', handlePurchaseRequestCreated);
    socket.on('purchase_request_approved', handlePurchaseRequestApproved);
    socket.on('direct_message:unread_count', handleUnreadCountUpdate);

    return () => {
      socket.off('purchase_request_updated', handlePurchaseRequestUpdate);
      socket.off('purchase_request_created', handlePurchaseRequestCreated);
      socket.off('purchase_request_approved', handlePurchaseRequestApproved);
      socket.off('direct_message:unread_count', handleUnreadCountUpdate);
    };
  }, [socket, isConnected, refreshSidebarCounts]);

  const loadCounts = async () => {
    try {
      const counts = {};
      let procurementTotal = 0;
      
      // Pending Approval count
      try {
        const resPending = await purchaseRequestService.getPendingApprovals();
        const pendingCount = resPending?.data?.data?.total || (resPending?.data?.data?.requests?.length ?? 0);
        counts['/purchase-requests/pending-approval'] = pendingCount;
        procurementTotal += pendingCount;
      } catch (e) {}
      
      // Procurement Approval count
      try {
        const resProc = await purchaseRequestService.getPendingProcurement();
        const procCount = resProc?.data?.data?.total || (resProc?.data?.data?.requests?.length ?? 0);
        counts['/purchase-requests/procurement'] = procCount;
        procurementTotal += procCount;
      } catch (e) {}
      
      // Payment Process count (sum of finance approval + payment in progress)
      try {
        const resFin = await purchaseRequestService.getPendingFinance();
        const resPay = await purchaseRequestService.getAllRequests({ status: 'payment_in_progress', current_stage: 'pay_vendor_stage', show_all: true });
        const finCount = resFin?.data?.data?.total || (resFin?.data?.data?.requests?.length ?? 0);
        const payCount = resPay?.data?.data?.total || (resPay?.data?.data?.requests?.length ?? 0);
        const paymentTotal = (finCount || 0) + (payCount || 0);
        counts['/purchase-requests/finance'] = paymentTotal;
        procurementTotal += paymentTotal;
      } catch (e) {}
      
      // Set total for main Procurement module
      counts['/purchase-requests'] = procurementTotal;
      
      // GMD Approval count
      try {
        const resGMD = await purchaseRequestService.getAllRequests({ 
          status: 'pending_md_approval',
          current_stage: 'md_approval_stage',
          show_all: true
        });
        const gmdCount = resGMD?.data?.data?.total || (resGMD?.data?.data?.requests?.length ?? 0);
        counts['/gmd/dashboard'] = gmdCount;
      } catch (e) {}
      
      // Unread messages count for Collaboration
      try {
        const resMessages = await api.get('/direct-messaging/unread-count');
        const count = resMessages?.data?.data?.count || 0;
        counts['/collaboration'] = count;
        counts['/collaboration/messages'] = count;
      } catch (e) {
        console.error('Failed to fetch unread messages count:', e);
      }
      setBadgeCounts(counts);
    } catch (err) {
      // silent fail
    }
  };

  const loadUserModules = () => {
    try {
      // First, try to use permissions from user data directly if available
      if (user?.UserType?.sidebar_modules) {
        const allowedModules = [];
        console.log('Loading modules from user type:', user.UserType.sidebar_modules.length, 'modules');
        console.log('Raw sidebar_modules:', JSON.stringify(user.UserType.sidebar_modules, null, 2));
        
        user.UserType.sidebar_modules.forEach(moduleData => {
          const moduleId = moduleData.module_id;
          const moduleConfig = allModules[moduleId];
          
          console.log(`Processing module: ${moduleId}, enabled: ${moduleData.enabled}, config exists: ${!!moduleConfig}`);
          
          if (moduleConfig && moduleData.enabled) {
            const allowedItemIds = moduleData.items || [];
            const hasSubItems = moduleConfig.subItems && moduleConfig.subItems.length > 0;
            
            // Filter based on items array if it exists and has values
            if (hasSubItems && allowedItemIds.length > 0) {
              // Filter sub-items based on allowed IDs; items marked sharesPermission
              // show for anyone allowed their permissionId
              const filteredSubItems = moduleConfig.subItems.filter(subItem =>
                allowedItemIds.includes(subItem.id) ||
                (subItem.sharesPermission && allowedItemIds.includes(subItem.permissionId))
              );
              
              console.log(`Module ${moduleId}: ${allowedItemIds.length} allowed IDs, ${filteredSubItems.length}/${moduleConfig.subItems.length} matches`);
              
              // Include module only if there are matching sub-items
              if (filteredSubItems.length > 0) {
                allowedModules.push({
                  ...moduleConfig,
                  subItems: filteredSubItems
                });
              }
            } else {
              // No items restriction - if items array missing or empty, hide sub-items unless module has no subItems
              // For safety, if the module has subItems but items is empty, do not expose all by default
              const safeSubItems = allowedItemIds.length === 0 && hasSubItems ? [] : (moduleConfig.subItems || []);
              console.log(`Module ${moduleId}: Applying safe filtering (${safeSubItems.length} items shown)`);
              // Only push module if either it has no subItems or there are safeSubItems to show
              if (!hasSubItems || safeSubItems.length > 0) {
                allowedModules.push({
                  ...moduleConfig,
                  subItems: safeSubItems
                });
              }
            }
          }
        });
        
        console.log('Final modules to display:', allowedModules.length);
        allowedModules.forEach(m => {
          console.log(`  - ${m.id}: ${m.subItems.length} items`);
        });
        
        setUserModules(allowedModules);
        setExpandedItems({});
        return;
      }
      
      // Fallback: Use permissions from AuthContext
      if (permissions && permissions.modules) {
        const allowedModules = [];
        
        // Iterate through user's modules and filter sub-items based on permissions
        Object.keys(permissions.modules).forEach(moduleId => {
          const moduleConfig = allModules[moduleId];
          
          if (moduleConfig && hasModule(moduleId)) {
            // Filter sub-items based on user's specific item permissions
            const filteredSubItems = filterSubItemsByPermissions(
              moduleConfig.subItems, 
              permissions
            );
            
            // Only include the module if user has access to at least one sub-item
            // or if it's a module without sub-items
            if (filteredSubItems.length > 0 || moduleConfig.subItems.length === 0) {
              allowedModules.push({
                ...moduleConfig,
                subItems: filteredSubItems
              });
            }
          }
        });
        
        setUserModules(allowedModules);
        
        // All modules start collapsed by default
        setExpandedItems({});
      } else {
        // Fallback: show only self-service if no permissions
        const selfServiceModule = allModules['self-service'];
        if (selfServiceModule) {
          setUserModules([{
            ...selfServiceModule,
            subItems: [] // No sub-items without proper permissions
          }]);
        }
      }
    } catch (error) {
      console.error('Error loading user modules:', error);
      // Fallback: show only self-service on error
      const selfServiceModule = allModules['self-service'];
      if (selfServiceModule) {
        setUserModules([{
          ...selfServiceModule,
          subItems: []
        }]);
      }
    }
  };

  const toggleExpanded = (moduleId) => {
    setExpandedItems(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isModuleActive = (module) => {
    if (isActive(module.path)) return true;
    return module.subItems.some(subItem => isActive(subItem.path));
  };

  const handleModuleMouseEnter = (moduleId, event) => {
    if (!isCollapsed) return;

    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    setHoveredModule(moduleId);
    setHoveredModuleRect(rect);
  };

  const handleModuleMouseLeave = () => {
    if (!isCollapsed) return;

    // Delay closing to allow mouse to move to flyout
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredModule(null);
      setHoveredModuleRect(null);
    }, 120);
  };

  const handleFlyoutMouseEnter = () => {
    // Clear close timeout when mouse enters flyout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleFlyoutMouseLeave = () => {
    // Close flyout when mouse leaves with delay
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredModule(null);
      setHoveredModuleRect(null);
    }, 120);
  };

  const handleCloseFlyout = () => {
    // Immediate close (called when clicking a submodule)
    setHoveredModule(null);
    setHoveredModuleRect(null);
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  return (
    <>
      {/* Sidebar - Unicorn Theme */}
      <div 
        className={`h-screen flex flex-col transition-all duration-300 ease-in-out shadow-2xl ${
          isCollapsed ? 'w-16' : 'w-64'
        }`}
        style={{
          background: 'linear-gradient(to bottom, #16314C, #0d1f30)',
          borderRight: '1px solid #DF383F',
          borderBottomRightRadius: '24px'
        }}
      >
        {/* Toggle button at top */}
        <div className="p-3 border-b flex justify-end" style={{ 
          borderColor: 'rgba(42, 74, 102, 0.5)',
          opacity: isCollapsed && hoveredModule ? 0 : 1,
          pointerEvents: isCollapsed && hoveredModule ? 'none' : 'auto',
          transition: 'opacity 150ms'
        }}>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-all"
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              color: '#C8D6E8'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
              e.currentTarget.style.color = '#E8EFF6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
              e.currentTarget.style.color = '#C8D6E8';
            }}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Navigation - Scrollable */}
        <nav 
          className={`flex-1 overflow-y-auto px-2 py-2 ${isCollapsed ? 'flex flex-col justify-center items-center gap-2.5' : ''}`}
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(223, 107, 56, 0.45) transparent'
          }}
        >
          <div className={isCollapsed ? 'flex flex-col gap-2.5 w-full' : 'space-y-1.5'}>
            {userModules.map((module) => {
              const Icon = module.icon;
              const hasSubItems = module.subItems && module.subItems.length > 0;
              const isExpanded = expandedItems[module.id];
              const moduleActive = isModuleActive(module);

              if (isCollapsed) {
                // Collapsed mode - icon only
                return (
                  <div
                    key={module.id}
                    ref={(el) => (moduleRefs.current[module.id] = el)}
                    className="flex items-center justify-center h-10 rounded-xl cursor-pointer transition-all duration-150 relative"
                    style={{
                      background: moduleActive ? '#fef5f0' : 'rgba(255, 255, 255, 0.04)',
                      color: moduleActive ? '#DF6B38' : '#C8D6E8'
                    }}
                    onMouseEnter={(e) => {
                      handleModuleMouseEnter(module.id, e);
                      if (!moduleActive) {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      handleModuleMouseLeave();
                      if (!moduleActive) {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
                      }
                    }}
                    onClick={() => {
                      if (hasSubItems) {
                        navigate(module.subItems[0].path);
                        scrollMainToTop('smooth');
                      } else {
                        navigate(module.path);
                        scrollMainToTop('smooth');
                      }
                    }}
                  >
                    <Icon className="h-5 w-5" />
                    {badgeCounts[module.path] > 0 && (
                      <span 
                        className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[9px] font-bold rounded-full"
                        style={{
                          background: '#DF6B38',
                          color: 'white',
                          minWidth: '18px',
                          textAlign: 'center'
                        }}
                      >
                        {badgeCounts[module.path]}
                      </span>
                    )}
                  </div>
                );
              }

              // Expanded mode - full display
              return (
                <div key={module.id}>
                  {/* Main module item */}
                  <div
                    className="group flex items-center gap-2.5 h-10 px-3.5 rounded-xl cursor-pointer transition-all duration-150"
                    style={{
                      background: moduleActive ? '#fef5f0' : 'transparent',
                      borderLeft: moduleActive ? '2px solid #DF6B38' : 'none',
                      paddingLeft: moduleActive ? '12px' : '14px',
                      color: moduleActive ? '#16314C' : '#E8EFF6'
                    }}
                    onMouseEnter={(e) => {
                      if (!moduleActive) {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!moduleActive) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                    onClick={() => {
                      if (hasSubItems) {
                        navigate(module.subItems[0].path);
                        scrollMainToTop('smooth');
                        setExpandedItems(prev => ({
                          ...prev,
                          [module.id]: true
                        }));
                      }
                    }}
                  >
                    {hasSubItems ? (
                      <div className="flex items-center flex-1 gap-2.5">
                        <div className="flex-shrink-0">
                          <Icon className="h-5 w-5" style={{
                            color: moduleActive ? '#DF6B38' : '#C8D6E8'
                          }} />
                        </div>
                        <span className="text-sm font-medium flex-1">{module.name}</span>
                        {badgeCounts[module.path] > 0 && (
                          <span className="px-2 py-0.5 text-[10px] font-bold rounded-full" style={{
                            background: moduleActive ? 'rgba(223, 107, 56, 0.2)' : '#DF6B38',
                            color: moduleActive ? '#DF6B38' : 'white'
                          }}>
                            {badgeCounts[module.path]}
                          </span>
                        )}
                      </div>
                    ) : (
                      <Link
                        to={module.path}
                        className="flex items-center flex-1 gap-2.5"
                        onClick={() => scrollMainToTop('smooth')}
                      >
                        <div className="flex-shrink-0">
                          <Icon className="h-5 w-5" style={{
                            color: moduleActive ? '#DF6B38' : '#C8D6E8'
                          }} />
                        </div>
                        <span className="text-sm font-medium flex-1">{module.name}</span>
                        {badgeCounts[module.path] > 0 && (
                          <span className="px-2 py-0.5 text-[10px] font-bold rounded-full" style={{
                            background: moduleActive ? 'rgba(223, 107, 56, 0.2)' : '#DF6B38',
                            color: moduleActive ? '#DF6B38' : 'white'
                          }}>
                            {badgeCounts[module.path]}
                          </span>
                        )}
                      </Link>
                    )}
                    
                    {hasSubItems && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(module.id);
                        }}
                        className="p-1"
                      >
                        <ChevronRight 
                          className="h-4 w-4 transition-transform duration-200" 
                          style={{
                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            color: moduleActive ? '#DF6B38' : '#C8D6E8'
                          }}
                        />
                      </button>
                    )}
                  </div>

                  {/* Sub-items */}
                  {hasSubItems && isExpanded && (
                    <div className="ml-11 mt-1 space-y-1 mb-1">
                      {module.subItems.map((subItem) => {
                        const subActive = isActive(subItem.path);
                        return (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            className="flex items-center justify-between px-3 py-1.5 rounded-md text-xs transition-all duration-150"
                            style={{
                              background: subActive ? 'rgba(223, 107, 56, 0.15)' : 'transparent',
                              color: subActive ? '#DF6B38' : '#8AAAC4',
                              fontWeight: subActive ? '500' : '400',
                              borderLeft: subActive ? '2px solid #DF6B38' : 'none',
                              paddingLeft: subActive ? '10px' : '12px'
                            }}
                            onMouseEnter={(e) => {
                              if (!subActive) {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                                e.currentTarget.style.color = '#E8EFF6';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!subActive) {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = '#8AAAC4';
                              }
                            }}
                            onClick={() => scrollMainToTop('smooth')}
                          >
                            <span>{subItem.name}</span>
                            {['/purchase-requests/pending-approval','/purchase-requests/procurement','/purchase-requests/finance','/gmd/dashboard'].includes(subItem.path) && badgeCounts[subItem.path] > 0 && (
                              <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-bold" style={{
                                background: '#DF6B38',
                                color: 'white'
                              }}>
                                {badgeCounts[subItem.path]}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        {/* Footer - Fixed at bottom */}
        {!isCollapsed && (
          <div className="p-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(42, 74, 102, 0.5)' }}>
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl" style={{
              background: 'rgba(255, 255, 255, 0.05)'
            }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-semibold text-sm" style={{
                background: 'linear-gradient(135deg, #DF6B38, #DF383F)',
                boxShadow: '0 4px 12px rgba(223, 107, 56, 0.3)'
              }}>
                {user?.first_name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: '#E8EFF6' }}>
                  {user?.first_name || 'User'}
                </p>
                <p className="text-xs truncate" style={{ color: '#8AAAC4' }}>{user?.UserType?.name || 'User'}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Flyout panel for collapsed sidebar */}
      {isCollapsed && hoveredModule && hoveredModuleRect && (
        <SidebarFlyout
          module={userModules.find(m => m.id === hoveredModule)}
          iconRect={hoveredModuleRect}
          onMouseEnter={handleFlyoutMouseEnter}
          onMouseLeave={handleFlyoutMouseLeave}
          onClose={handleCloseFlyout}
          badgeCounts={badgeCounts}
        />
      )}

      {/* Scrollbar styles */}
      <style>{`
        nav::-webkit-scrollbar {
          width: 4px;
        }
        nav::-webkit-scrollbar-track {
          background: transparent;
        }
        nav::-webkit-scrollbar-thumb {
          background: rgba(223, 107, 56, 0.45);
          border-radius: 2px;
        }
      `}</style>
    </>
  );
};

export default DynamicSidebar;
